import React, { useMemo, useState } from 'react';
import { Filter, Search, X } from 'lucide-react';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { ListSortMenu, type ListSortOption } from '@/shared/components/ListSortMenu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  formatLocationLabel,
  type AreaTagSetMinimal,
  type LocationMinimal,
  type ObservationFilters as ObservationFiltersType,
  type ObservationPriority,
  type ObservationSortKey,
  type ObservationSortState,
  type ObservationStatus,
} from '../types';

interface Props {
  filters: ObservationFiltersType;
  onFiltersChange: (filters: ObservationFiltersType) => void;
  totalCount: number;
  filteredCount: number;
  sort: ObservationSortState[];
  onSortChange: (sort: ObservationSortState[]) => void;
  areaTags: LocationMinimal[];
  areaTagSets: AreaTagSetMinimal[];
}

const SORT_OPTIONS: readonly ListSortOption<ObservationSortKey>[] = [
  { key: 'number', label: 'Number' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'assignee', label: 'Assignee' },
  { key: 'due_date', label: 'Due date' },
  { key: 'created_at', label: 'Created' },
];

export const ObservationsFilters: React.FC<Props> = ({
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
  sort,
  onSortChange,
  areaTags,
  areaTagSets,
}) => {
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const activeFilterCount = useMemo(
    () =>
      [
        filters.status,
        filters.priority,
        filters.assignee,
        filters.area_tag_id,
        filters.area_tag_set_id,
      ].filter(Boolean).length,
    [
      filters.area_tag_id,
      filters.area_tag_set_id,
      filters.assignee,
      filters.priority,
      filters.status,
    ]
  );
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.search ||
          filters.status ||
          filters.priority ||
          filters.assignee ||
          filters.area_tag_id ||
          filters.area_tag_set_id
      ),
    [filters]
  );

  const update = (patch: Partial<ObservationFiltersType>) =>
    onFiltersChange({ ...filters, ...patch });
  const clearDialogFilters = () =>
    onFiltersChange(filters.search ? { search: filters.search } : {});

  return (
    <div className="space-y-3" data-tour-id="observations-filters">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1 md:max-w-[320px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search observations…"
            value={filters.search ?? ''}
            onChange={(e) => update({ search: e.target.value || undefined })}
            className="w-full pl-8"
          />
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="hidden gap-2 md:inline-flex"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>

        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="hidden md:inline-flex"
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFiltersChange({})}
            className="hidden gap-1 md:inline-flex"
          >
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        )}

        <span className="ml-auto hidden text-sm text-muted-foreground md:inline">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-md border bg-muted/30 px-3 py-2 md:hidden">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={() => setFilterDialogOpen(true)}
        >
          <Filter className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
        </Button>
        <ListSortMenu
          sort={sort}
          options={SORT_OPTIONS}
          onSortChange={onSortChange}
          className="w-full"
        />
      </div>

      <div className="flex items-center justify-between gap-3 md:hidden">
        {hasActiveFilters ? (
          <Button variant="ghost" size="sm" onClick={() => onFiltersChange({})} className="gap-1">
            <X className="h-4 w-4" />
            Clear Filters
          </Button>
        ) : (
          <span />
        )}
        <span className="text-sm text-muted-foreground">
          Showing {filteredCount} of {totalCount}
        </span>
      </div>

      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Observation Filters</DialogTitle>
            <DialogDescription>Refine the observations shown on this page.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <FilterField label="Status">
              <Select
                value={filters.status ?? 'all'}
                onValueChange={(value) =>
                  update({ status: value === 'all' ? undefined : (value as ObservationStatus) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {(Object.keys(STATUS_LABELS) as ObservationStatus[]).map((status) => (
                    <SelectItem key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Priority">
              <Select
                value={filters.priority ?? 'all'}
                onValueChange={(value) =>
                  update({ priority: value === 'all' ? undefined : (value as ObservationPriority) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All priorities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {(Object.keys(PRIORITY_LABELS) as ObservationPriority[]).map((priority) => (
                    <SelectItem key={priority} value={priority}>
                      {PRIORITY_LABELS[priority]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Unit/Location">
              <Select
                value={filters.area_tag_id ? String(filters.area_tag_id) : 'all'}
                onValueChange={(value) =>
                  update({ area_tag_id: value === 'all' ? undefined : Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All units/locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All units/locations</SelectItem>
                  {areaTags.map((areaTag) => (
                    <SelectItem key={areaTag.id} value={String(areaTag.id)}>
                      {formatLocationLabel(areaTag)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>

            <FilterField label="Phase/Zone">
              <Select
                value={filters.area_tag_set_id ? String(filters.area_tag_set_id) : 'all'}
                onValueChange={(value) =>
                  update({ area_tag_set_id: value === 'all' ? undefined : Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="All phases/zones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All phases/zones</SelectItem>
                  {areaTagSets.map((areaTagSet) => (
                    <SelectItem key={areaTagSet.id} value={String(areaTagSet.id)}>
                      {areaTagSet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterField>
          </div>

          <DialogFooter className="gap-2 sm:space-x-0">
            <Button
              variant="outline"
              onClick={clearDialogFilters}
              disabled={activeFilterCount === 0}
            >
              Clear All
            </Button>
            <DialogClose asChild>
              <Button>Done</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

export default ObservationsFilters;
